import { useCallback, useEffect, useRef } from 'react';

import type { DropdownMenuPlacement } from 'components/Dropdown/types/DropdownMenuPlacement';
import { useLatest } from 'hooks/useLatest';
import type { SearchRefinementType } from 'modules/search/constants/refinements/searchRefinementTypes';
import { useSearchAnalytics } from 'modules/search/hooks/useSearchAnalytics';
import { trackEvent } from 'utils/analytics/track/trackEvent';

export function useSearchRefinementDropdown(
  facetType: SearchRefinementType,
  hasSelectedValues: boolean,
  initialPlacement: DropdownMenuPlacement = 'bottom-left',
) {
  const dropdownIsOpenRef = useRef(false);
  const hasOtherOpenDropdownsRef = useRef(false);

  const holderRef = useRef<HTMLDivElement>(null);
  const { getSearchTrackingData } = useSearchAnalytics();

  const dropdownStatusRef = useRef(hasSelectedValues ? 'PINNED' : 'UNPINNED');
  const currentPlacementRef = useLatest(initialPlacement);

  const resizeHandler = useCallback(() => {
    if (holderRef.current) {
      const menu = holderRef.current.querySelector('menu');

      if (menu && currentPlacementRef.current) {
        const { left, right } = menu.getBoundingClientRect();

        let newPlacement: DropdownMenuPlacement | null = null;

        if (left < 0) {
          newPlacement = currentPlacementRef.current.includes('left')
            ? (currentPlacementRef.current.replace(
                'left',
                'right',
              ) as DropdownMenuPlacement)
            : currentPlacementRef.current;
        } else if (right > window.innerWidth) {
          newPlacement = currentPlacementRef.current.includes('right')
            ? (currentPlacementRef.current.replace(
                'right',
                'left',
              ) as DropdownMenuPlacement)
            : currentPlacementRef.current;
        }

        if (newPlacement && newPlacement !== currentPlacementRef.current) {
          menu.classList.remove(`placement-${currentPlacementRef.current}`);
          menu.classList.add(`placement-${newPlacement}`);
          currentPlacementRef.current = newPlacement;
        } else if (
          !newPlacement &&
          currentPlacementRef.current !== initialPlacement
        ) {
          menu.classList.remove(`placement-${currentPlacementRef.current}`);
          menu.classList.add(`placement-${initialPlacement}`);
          currentPlacementRef.current = initialPlacement;
        }
      }
    }
  }, [currentPlacementRef, initialPlacement]);

  const toggleDropdown = useCallback(
    (open: boolean, detailsEl: HTMLDetailsElement | null) => {
      dropdownIsOpenRef.current = open;

      if (!holderRef.current) {
        throw new Error(
          `Search filter dropdown for ${facetType} does not exist.`,
        );
      }

      const { dataset } = holderRef.current;

      const filterIsPinned = dataset.facetState === 'PINNED';
      const filterIsUnpinned = dataset.facetState === 'UNPINNED';
      const otherOpenDropdowns = Array.from(
        document.querySelectorAll(
          `[data-facet-state] details[open]:not(:has([data-facet-type=${facetType}])`,
        ),
      );

      hasOtherOpenDropdownsRef.current = otherOpenDropdowns.length > 0;

      if (!open) {
        document.documentElement.style.removeProperty(
          '--search-refinements-active-dropdown-height',
        );
        if (hasSelectedValues && filterIsUnpinned) {
          dataset.facetState = 'QUEUED';

          if (!hasOtherOpenDropdownsRef.current) {
            const filtersToPin = document.querySelectorAll<HTMLDivElement>(
              '[data-facet-state="QUEUED"]:has(input:checked:not([value=""])), [data-facet-state="QUEUED"]:has(input[name="startsLT"]:not([value=""]), input[name="endsGT"]:not([value=""]))',
            );

            filtersToPin.forEach((node) => {
              // eslint-disable-next-line no-param-reassign
              node.dataset.facetState = 'PINNED';
            });
          }
        } else if (!hasSelectedValues && filterIsPinned) {
          dataset.facetState = 'QUEUED';

          if (!hasOtherOpenDropdownsRef.current) {
            const filtersToUnpin = document.querySelectorAll<HTMLDivElement>(
              '[data-facet-state="QUEUED"]:not(:has(input:checked:not([value=""]))), [data-facet-state="QUEUED"]:not(:has(input[name="startsLT"]:not([value=""]), input[name="endsGT"]:not([value=""])))',
            );

            filtersToUnpin.forEach((node) => {
              // eslint-disable-next-line no-param-reassign
              node.dataset.facetState = 'UNPINNED';
            });
          }
        }
      } else {
        const menuNode = detailsEl ? detailsEl.querySelector('menu') : null;

        const menuHeight = menuNode
          ? menuNode.getBoundingClientRect().height
          : 0;

        document.documentElement.style.setProperty(
          '--search-refinements-active-dropdown-height',
          `${menuHeight}px`,
        );

        trackEvent('Expanded Search Filter', {
          filter_name: `${facetType}Facet`,
          ...getSearchTrackingData(),
        });
      }
    },
    [facetType, getSearchTrackingData, hasSelectedValues],
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      resizeHandler();
      window.addEventListener('resize', resizeHandler);
      return () => {
        window.removeEventListener('resize', resizeHandler);
      };
    }
  }, [resizeHandler]);

  return {
    placement: currentPlacementRef.current,
    holderRef,
    toggleDropdown,
    dropdownStatus: dropdownStatusRef.current,
  };
}
